import React, { useEffect, useMemo,useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import {
	usePlaidLink
} from 'react-plaid-link';

import { createLog, LogSeverity } from '@ekhodealer/ekho-common';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Box,
	ConnectBankAccount,
	ConnectedBankInfo,
	Popup,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import type { BankAccount, ConsentData} from '@ekhodealer/ekho-common';
import type {
	PlaidLinkError,
	PlaidLinkOnExitMetadata,	PlaidLinkOnSuccessMetadata} from 'react-plaid-link';

import {
	useCreateBankAccountsFromPlaidData,
	useCreateLinkToken,
	useGetBankAccounts,
	useSetPrimaryBankAccount,
} from '@common/api/plaid/plaidService';

import BankAccountManagement from './BankAccountManagement';

interface BankAccountOnboardingProps {
	servicesProviderId: string;
	onComplete?: (bankAccount: BankAccount | null) => void;
	onCancel?: () => void;
	authToken?: string;
	apiBaseUrl?: string;
	className?: string;
}

const BankAccountOnboarding: React.FC<BankAccountOnboardingProps> = ({
	servicesProviderId,
	//onCancel,
}) => {
	const [plaidError, setPlaidError] = useState<string | null>(null);
	const [isLinkingAccount, setIsLinkingAccount] = useState<boolean>(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
	// React Query hooks
	const {
		data: bankAccountsData = [],
		isLoading: isLoadingBankAccounts,
		error: bankAccountsError,
	} = useGetBankAccounts(servicesProviderId);

	const {
		data: linkTokenData,
		isLoading: isLoadingLinkToken,
		error: linkTokenError,
		refetch: refetchLinkToken,
	} = useCreateLinkToken(servicesProviderId);

	const createBankAccountsMutation = useCreateBankAccountsFromPlaidData();
	const setPrimaryBankAccountMutation = useSetPrimaryBankAccount();

	// Derived state
	const hasBankAccounts = bankAccountsData.length > 0;
	const primaryBankAccount = useMemo(
		() => bankAccountsData.find((account) => account.primary) || null,
		[bankAccountsData],
	);

	// Combined loading states
	const isInitialLoading = isLoadingBankAccounts || (isLoadingLinkToken && !hasBankAccounts);
	const isCreatingAccount = createBankAccountsMutation.isPending || isLinkingAccount;
	const isUpdatingPrimary = setPrimaryBankAccountMutation.isPending;

	// Combined error handling
	const combinedError = useMemo(() => {
		if (plaidError) return plaidError;
		if (createBankAccountsMutation.error) {
			const error = createBankAccountsMutation.error;
			return error instanceof Error ? error.message : 'Failed to create bank account';
		}
		if (setPrimaryBankAccountMutation.error) {
			const error = setPrimaryBankAccountMutation.error;
			return error instanceof Error ? error.message : 'Failed to update primary bank account';
		}
		if (linkTokenError) {
			const error = linkTokenError;
			return error instanceof Error ? error.message : 'Failed to initialize bank connection';
		}
		if (bankAccountsError) {
			const error = bankAccountsError;
			return error instanceof Error ? error.message : 'Failed to load bank accounts';
		}
		return null;
	}, [
		plaidError,
		createBankAccountsMutation.error,
		setPrimaryBankAccountMutation.error,
		linkTokenError,
		bankAccountsError,
	]);

	const getConsentData = async (): Promise<ConsentData> => {
		try {
			return {
				userAgent: navigator.userAgent || 'unknown',
				timestamp: new Date().toISOString(),
				ipAddress: 'unknown',
			};
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to collect consent data: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			// Return fallback data if IP collection fails
			return {
				userAgent: navigator.userAgent || 'unknown',
				timestamp: new Date().toISOString(),
				ipAddress: 'unknown',
			};
		}
	};

	const handleEditBankInfo = (): void => {
		const isOpen = isEditModalOpen;
		if (isOpen) {
			setIsEditModalOpen(false);
		} else {
			setIsEditModalOpen(true);
		}
	};

	// API call to save bank account after Plaid success
	const saveBankAccount = async (
		publicToken: string,
		metadata: PlaidLinkOnSuccessMetadata,
	): Promise<void> => {
		setIsLinkingAccount(true);
		setPlaidError(null);

		try {
			// Collect consent data (IP address + user agent)
			const consentData: ConsentData = await getConsentData();

			const bankAccounts = await createBankAccountsMutation.mutateAsync({
				input: {
					servicesProviderId,
					publicToken,
					plaidMetadata: metadata,
				},
				consentData,
			});

			if (bankAccounts.length > 1) {
				handleEditBankInfo();
			}
		} catch (err) {
			createLog(
				'config-mgmt',
				`Error in saveBankAccount: ${err instanceof Error ? err.message : String(err)}`,
				LogSeverity.ERROR,
			);
			const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
			setPlaidError(errorMessage);
		} finally {
			setIsLinkingAccount(false);
		}
	};

	const handleSetPrimary = async (bankAccountId: string): Promise<void> => {
		setPlaidError(null);

		try {
			await setPrimaryBankAccountMutation.mutateAsync({
				servicesProviderId,
				bankAccountId,
			});
		} catch (err) {
			createLog(
				'config-mgmt',
				`Error setting primary bank account: ${err instanceof Error ? err.message : String(err)}`,
				LogSeverity.ERROR,
			);
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to update primary bank account';
			setPlaidError(errorMessage);
		}
	};

	// Use the real Plaid Link hook
	const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
		token: linkTokenData?.link_token || null,
		onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
			saveBankAccount(publicToken, metadata);
		},
		onExit: (err: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata | null) => {
			if (err) {
				createLog(
					'config-mgmt',
					`Plaid Link error: ${err.error_message || 'Unknown error'}`,
					LogSeverity.ERROR,
				);
				setPlaidError(`Bank connection failed: ${err.error_message || 'Unknown error'}`);
			}
			// Plaid Link exit is informational, metadata contains exit reason
			void metadata;
		},
	});

	const handlePlaidLinkOpen = (): void => {
		if (plaidReady && linkTokenData?.link_token) {
			openPlaidLink();
		} else if (!linkTokenData?.link_token && !isLoadingLinkToken) {
			// If no link token and not loading, try to fetch one
			refetchLinkToken();
		}
	};

	// Clear errors when data changes successfully
	useEffect(() => {
		if (createBankAccountsMutation.isSuccess || setPrimaryBankAccountMutation.isSuccess) {
			setPlaidError(null);
		}
	}, [createBankAccountsMutation.isSuccess, setPrimaryBankAccountMutation.isSuccess]);

	const payoutTimelines = (
		<Accordion>
			<AccordionHeader>
				<Text size='bodySmall' color='secondary'>
					Payout timelines
				</Text>
			</AccordionHeader>
			<AccordionBody itemsSpacing='4'>
				<Text size='bodySmall' color='secondary'>
					Card payments: paid out instantly once payment clears.
				</Text>
				<Text size='bodySmall' color='secondary'>
					Bank payments: payouts arrive within 1–3 business days.
				</Text>
				<Text size='bodySmall' color='secondary'>
					Financed orders: payouts released within 5 business days after fulfillment.
				</Text>
				<Text size='bodySmall' color='secondary'>
					Cancellations: refund amounts are automatically debited.
				</Text>
			</AccordionBody>
		</Accordion>
	);

	return (
		<>
			<Box padding='24' background='secondary' rounding='12'>
				<Stack itemsSpacing='16'>
					<Text size='body' weight='semibold'>Bank account connection</Text>
					{payoutTimelines}
					{isInitialLoading ? (
						<ConnectBankAccount
							onConnectBank={() => {}}
							isLoading={true}
							checkmarkList={[]}
							title={null}
							description='Loading bank account information...'
						/>
					) : !hasBankAccounts || !primaryBankAccount ? (
						<ConnectBankAccount
							onConnectBank={handlePlaidLinkOpen}
							checkmarkList={[]}
							isLoading={isCreatingAccount || !plaidReady}
							errorTitle={combinedError ? 'Connection Error' : undefined}
							errorDescription={combinedError || undefined}
							title={null}
							description=''
						/>
					) : (
						<ConnectedBankInfo
							bankAccountInfo={{
								bankName: primaryBankAccount.bankName || 'Bank Account',
								accountNumberLastDigits:
									primaryBankAccount.accountName || '****',
							}}
							onEditBankInfo={handleEditBankInfo}
							isLoading={isCreatingAccount || isUpdatingPrimary}
							title={
								isCreatingAccount
									? 'Updating bank account'
									: isUpdatingPrimary
										? 'Updating primary account'
										: 'Primary bank account'
							}
						/>
					)}
				</Stack>
			</Box>

			{/* Hidden popup trigger */}
			<div style={{ display: 'none' }}>
				<DialogTrigger isOpen={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
					<Popup title='Choose your payment account'>
						<div>
							<BankAccountManagement
								key='bank-account-management-stable'
								bankAccounts={bankAccountsData}
								onSetPrimary={handleSetPrimary}
								handlePlaidLinkOpen={handlePlaidLinkOpen}
								handleClose={handleEditBankInfo}
							/>
						</div>
					</Popup>
				</DialogTrigger>
			</div>
		</>
	);
};

export default BankAccountOnboarding;
